import { css } from '@emotion/react';
import { Box, List, ListItem, ListItemText, ListItemSecondaryAction } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { useColors } from 'hooks/useColors';
import { IColors } from 'types/colors';
import Radio from 'src/components/shared/Radio/Radio';

import { i18n } from 'lib/i18n';
import { mobileOnly } from 'styles/utils';

const listEventsCss = (isClickable: boolean, colors: IColors) => css`
	&.MuiList-root {
		display: flex;
		flex-direction: row;
		padding: 0;
		background-color: ${colors.bg_background_primary};
		margin-bottom: 20px;
		width: 100%;
	}

	.MuiListItem-root {
		align-items: flex-start;
	}

	li {
		align-self: center;
	}

	li .MuiListItemText-primary {
		color: ${colors.tx_text_secondary};
		font-size: 1rem;
	}

	li .MuiListItemText-secondary {
		color: ${colors.tx_text_primary};
		font-size: 1.6rem;
	}

	&:hover {
		cursor: ${isClickable && 'pointer'};
	}
`;

const listEventItemCss = css`
	display: flex;
	flex-direction: column;
`;

const eventStatusCss = (colors: IColors, isLateFee?: boolean) => css`
	align-self: flex-start;
	min-height: 25px;
	padding: 5px 8px;
	border-radius: 2px;
	background-color: ${isLateFee ? colors.bg_error : colors.btnGreenBg};
	font-size: 1.4rem;
	font-weight: bold;
	color: ${colors.tx_white};

	${mobileOnly} {
		min-width: 70px;
		padding: 2px 0px;
		text-align: center;
	}
`;

const listEventNameCss = css`
	.MuiListItemText-secondary {
		font-weight: bold;
	}
`;

const listEventLocationCss = css`
	.MuiListItemText-secondary {
		text-decoration: underline;
	}
`;

const dateAndTimeTextCss = css`
	.MuiListItemText-secondary {
		white-space: pre-line;
	}
`;

interface EventProps {
	id: number;
	eventName: string;
	eventLocation?: string;
	eventDatesRange: string;
	eventDateTime: string;
	selectEvent?: (id: number) => void;
	isEventSelected?: boolean;
	showRadio?: boolean;
	isEarlyBird?: boolean;
	isLateFee?: boolean;
}

function SeasonScheduleEvent({
	id,
	eventName,
	eventLocation,
	eventDatesRange,
	eventDateTime,
	isEarlyBird,
	selectEvent,
	isEventSelected,
	showRadio = false,
	isLateFee,
}: EventProps) {
	const selectEventHandler = () => {
		if (selectEvent) {
			selectEvent(id);
		}
	};
	const { colors } = useColors();

	return (
		<List css={listEventsCss(Boolean(selectEvent), colors)} onClick={selectEventHandler} data-testid="events-session">
			{showRadio && (
				<ListItem css={listEventItemCss}>
					<ListItemSecondaryAction>
						<Radio
							color="secondary"
							checked={isEventSelected}
							onChange={selectEventHandler}
							checkedIcon={<CheckCircleIcon />}
						/>
					</ListItemSecondaryAction>
				</ListItem>
			)}
			<ListItem css={listEventNameCss}>
				<ListItemText secondary={eventName.toUpperCase()} primary={i18n.event.eventName} />
				{isEarlyBird && <Box css={eventStatusCss(colors)}>{i18n.programs.registration.title.earlyBird}</Box>}
				{isLateFee && <Box css={eventStatusCss(colors, isLateFee)}>{i18n.programs.registration.title.late}</Box>}
			</ListItem>

			{eventLocation && (
				<ListItem css={listEventLocationCss}>
					<ListItemText secondary={eventLocation} primary={i18n.event.eventLocation} />
				</ListItem>
			)}

			<ListItem>
				<ListItemText secondary={eventDatesRange} primary={i18n.event.eventDate} />
			</ListItem>

			{/* dateTime &&*/}
			<ListItem css={dateAndTimeTextCss}>
				<ListItemText
				  secondary={eventDateTime} 
				  primary={i18n.event.eventTime} 
				/>
			</ListItem>
		</List>
	);
}

export default SeasonScheduleEvent;
